import { PageNotice } from '../../../shared/components/PageFeedback';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import EmptyAccessState from '@/app/components/EmptyAccessState';
import { PageFrame, PageHeader } from '@/shared/components/patterns';
import { useDashboardData } from '@/modules/dashboard/hooks/useDashboardData';
import { useTenant } from '@/tenant/tenant-provider';
import DashboardStatsGrid from '@/modules/dashboard/components/DashboardStatsGrid';
import AccountingSection from '@/modules/dashboard/components/AccountingSection';
import AssignmentsSection from '@/modules/dashboard/components/AssignmentsSection';
import DealsOverviewSection from '@/modules/dashboard/components/DealsOverviewSection';
import ClientsOverviewSection from '@/modules/dashboard/components/ClientsOverviewSection';
import OverdueDashboardView from '@/modules/dashboard/components/OverdueDashboardView';
import { DASHBOARD_SECTION_IDS } from '@/modules/dashboard/dashboard-sections';
import '@/modules/dashboard/styles/dashboard.css';

export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const { dashboard } = useTenant();
  const {
    rawData,
    computed,
    loading,
    error,
    dateRange,
    setDateRange,
    incarichiDateRange,
    setIncarichiDateRange,
    isCalendarOpen,
    setIsCalendarOpen,
    isIncarichiCalendarOpen,
    setIsIncarichiCalendarOpen,
    applyDatePreset,
    applyIncarichiPreset,
  } = useDashboardData();
  const visibleSectionIds = new Set(dashboard?.visibleSectionIds || []);
  const showStatsCards = visibleSectionIds.has(DASHBOARD_SECTION_IDS.STATS_CARDS);
  const showAccountingOverview = visibleSectionIds.has(
    DASHBOARD_SECTION_IDS.ACCOUNTING_OVERVIEW
  );
  const showAssignmentsOverview = visibleSectionIds.has(
    DASHBOARD_SECTION_IDS.ASSIGNMENTS_OVERVIEW
  );
  const showDealsOverview = visibleSectionIds.has(DASHBOARD_SECTION_IDS.DEALS_OVERVIEW);
  const showClientsOverview = visibleSectionIds.has(DASHBOARD_SECTION_IDS.CLIENTS_OVERVIEW);
  const showSecondaryGrid = showDealsOverview || showClientsOverview;
  const hasVisibleSections =
    showStatsCards ||
    showAccountingOverview ||
    showAssignmentsOverview ||
    showDealsOverview ||
    showClientsOverview;

  if (loading) {
    return <PageFrame><div className="dashboard-page__state dashboard-page__state--loading">Caricamento dashboard...</div></PageFrame>;
  }

  if (error) {
    return <PageFrame><PageNotice role="alert" className="dashboard-page__state dashboard-page__state--error wt-inline-error">Errore: {error}</PageNotice></PageFrame>;
  }

  if (!rawData || !computed) {
    return <PageFrame><div className="dashboard-page__state dashboard-page__state--empty">Nessun dato disponibile</div></PageFrame>;
  }

  if (searchParams.get('view') === 'overdue') {
    return (
      <PageFrame className="dashboard-page">
        <PageHeader title="Dashboard" description="Vista dedicata delle attività in ritardo" />
        <OverdueDashboardView items={computed.overdueItems} unavailableModules={computed.overdueUnavailableModules} />
      </PageFrame>
    );
  }

  if (!hasVisibleSections) {
    return (
      <EmptyAccessState
        title="Dashboard non disponibile"
        description="La configurazione corrente del tenant non espone sezioni dashboard visibili."
      />
    );
  }

  return (
    <PageFrame className="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Panoramica generale delle attività aziendali"
      />

      {showStatsCards ? <DashboardStatsGrid statsCards={computed.statsCards} /> : null}
      {showAccountingOverview ? (
        <AccountingSection
          accounting={computed.accounting}
          unavailable={computed.accountingUnavailable}
          lineChartData={computed.lineChartData}
          bilancioData={computed.bilancioData}
          incassiClientiData={computed.incassiClientiData}
          speseTipoData={computed.speseTipoData}
          dateRange={dateRange}
          setDateRange={setDateRange}
          isCalendarOpen={isCalendarOpen}
          setIsCalendarOpen={setIsCalendarOpen}
          applyDatePreset={applyDatePreset}
          dateRangeLabel={computed.dateRangeLabel}
        />
      ) : null}

      {showAssignmentsOverview ? (
        <AssignmentsSection
          incarichiStats={computed.incarichiStats}
          incarichiPerStatusData={computed.incarichiPerStatusData}
          incarichiPerMittenteData={computed.incarichiPerMittenteData}
          incarichiPerTipoData={computed.incarichiPerTipoData}
          incarichiPerUrgenzaData={computed.incarichiPerUrgenzaData}
          staffPerformance={computed.staffPerformance}
          staffPerformanceStatus={computed.staffPerformanceStatus}
          staffPerformanceError={computed.staffPerformanceError}
          incarichiDateRange={incarichiDateRange}
          setIncarichiDateRange={setIncarichiDateRange}
          isIncarichiCalendarOpen={isIncarichiCalendarOpen}
          setIsIncarichiCalendarOpen={setIsIncarichiCalendarOpen}
          applyIncarichiPreset={applyIncarichiPreset}
          incarichiDateRangeLabel={computed.incarichiDateRangeLabel}
        />
      ) : null}

      {showSecondaryGrid ? (
        <div className="dashboard-grid-two">
          {showDealsOverview ? (
            <DealsOverviewSection dealSummary={computed.dealSummary} />
          ) : null}
          {showClientsOverview ? (
            <ClientsOverviewSection clientsSummary={computed.clientsSummary} />
          ) : null}
        </div>
      ) : null}
    </PageFrame>
  );
}
